import { db } from "@/stores/indexed-db";
import { useSetTask } from "tinytick/ui-react";
import { useNetworkState } from "@uidotdev/usehooks";
import { useAuthValue } from "@/stores/auth-store";
import { useUserValue } from "@/stores/user-store";
import { toast } from "sonner";
import { client } from "@/utils/orpc";
import { isErrorResponse } from "@shamba-odk/types";

// Ensure requests never hang forever
const withTimeout = async <T,>(promise: Promise<T>, ms = 20000): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Request timed out after ${ms}ms`)), ms);
    promise
      .then((val) => {
        clearTimeout(timer);
        resolve(val);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
};

export const OutgrowerMonitoringStore = () => {
  const accessToken = useAuthValue("accessToken");
  const user = useUserValue("name") === "Administrator" ? useUserValue("name") : useUserValue("email");
  const network = useNetworkState();

  useSetTask(
    "syncOutgrowerMonitoring",
    async () => {
      if (!network.online || !accessToken || !user) {
        return;
      }

      await syncOutgrowerMonitoring(accessToken, user);
    },
    [accessToken],
    "sync",
    {
      retryDelay: 5000,
      maxRetries: 4,
    }
  );

  return null;
};

export const syncOutgrowerMonitoring = async (token: string, user: string) => {
  try {
    const unsynced = await db.outgrower_monitoring_questionnaires
      .where("status")
      .anyOf(["unsynced", "partial-sync"]) // incremental sync
      .toArray();

    if (unsynced.length === 0) {
      return;
    }

    let qIndex = 0;
    for (const q of unsynced) {
      qIndex++;

      const doc: Record<string, any> = {
        ...q,
        field_officer: user,
        // remove local-only/system fields for payload
        id: undefined,
        status: undefined,
        creation: undefined,
        modified: undefined,
        farm_photo: undefined,
        remote_name: undefined,
      };

      toast.info(`Syncing Questionnaire ${q.id ?? ""} (${qIndex}/${unsynced.length})`);

      try {
        let docName: string = q.remote_name || "";

        if (q.status === "partial-sync" && docName) {
          const updateRes = await withTimeout(client.frappe.updateDoc({
            accessToken: token,
            doctype: "Outgrower Monitoring Questionnaire",
            name: docName,
            doc,
          }), 20000);

          if (isErrorResponse(updateRes)) {
            throw new Error(updateRes.error.message || "Failed to update questionnaire record");
          }
        } else {
          const createRes = await withTimeout(client.frappe.createDoc({
            accessToken: token,
            doctype: "Outgrower Monitoring Questionnaire",
            doc,
          }), 20000);

          if (isErrorResponse(createRes)) {
            throw new Error(createRes.error.message || "Failed to create questionnaire record");
          }

          docName = createRes.data.data.name;
          await db.outgrower_monitoring_questionnaires.update(q.id as string, {
            status: "partial-sync",
            remote_name: docName,
          });
        }

        // Helper to upload a single file
        const uploadFile = async (file: File, fieldname: string): Promise<string | undefined> => {
          const uploadResponse = await withTimeout(client.frappe.uploadFile({
            accessToken: token,
            file,
            docname: docName,
            doctype: "Outgrower Monitoring Questionnaire",
            fieldname,
            file_name: `${docName}_${fieldname}`,
          }), 20000);

          if (isErrorResponse(uploadResponse)) {
            throw new Error(uploadResponse.error.message || `Failed to upload ${fieldname}`);
          }

          return uploadResponse.data.message?.file_url;
        };

        const isFileList = (value: any): value is FileList =>
          value && typeof value !== "string" && typeof value.length === "number" && typeof value.item === "function";

        const fileUpdates: Record<string, string> = {};

        if (q.farm_photo && isFileList(q.farm_photo) && q.farm_photo.length > 0) {
          const fileUrl = await uploadFile(q.farm_photo[0], "farm_photo");
          if (fileUrl) fileUpdates["farm_photo"] = fileUrl;
        }

        if (Object.keys(fileUpdates).length > 0) {
          const fileUpdateRes = await withTimeout(client.frappe.updateDoc({
            accessToken: token,
            doctype: "Outgrower Monitoring Questionnaire",
            name: docName,
            doc: fileUpdates,
          }), 20000);

          if (isErrorResponse(fileUpdateRes)) {
            throw new Error(fileUpdateRes.error.message || "Failed to update file references");
          }
        }

        const dbUpdate: Record<string, any> = {
          status: "synced",
          modified: new Date().toISOString(),
        };
        if (fileUpdates.farm_photo) dbUpdate.farm_photo = fileUpdates.farm_photo;

        await db.outgrower_monitoring_questionnaires.update(q.id as string, dbUpdate);

        toast.success(`Successfully synced Questionnaire ${q.id ?? ""} (${qIndex}/${unsynced.length})`);
      } catch (error) {
        console.error(`[omq] Error Processing Questionnaire ${q.id}:`, error);
        let errorMessage = `Failed to sync questionnaire ${q.id ?? ""}`;
        if (error instanceof Error) {
          errorMessage = error.message;
        }
        toast.error(`Error: ${errorMessage}. Continuing with next questionnaire.`);
        // continue to next item
      }
    }
  } catch (error) {
    console.error("[omq] Error Syncing Questionnaires:", error);
    toast.error("Error Syncing Questionnaires");
  }
};
